/**
 * Itinéraires : la route à moto entre deux points et la tournée optimisée d'un livreur, par la fonction `itineraire`
 * (service d'itinéraires auto-hébergé sur le serveur : Valhalla pour les trajets, VROOM pour les tournées ; données
 * © contributeurs OpenStreetMap).
 *
 * La route sert au PRIX des courses opérées depuis la 0066 : demander le trajet AVEC l'organisation le fait mémoriser
 * par le serveur, et la base le relit en calculant le prix. Sans réponse du service, la base prend l'estimation de
 * secours (vol d'oiseau × 1,56) et le détail de la course le dit.
 */
import { appelerFonction } from './supabase'
import { distanceRepliKm } from './prixOpere'

export interface Point {
  lat: number
  lng: number
}

export interface Trajet {
  distance_km: number
  duree_s: number | null
  /** Tracé encodé (voir `decoderTrace`), absent pour une estimation. */
  trace: string | null
  trace_precision: number
  source: 'valhalla' | 'estimation'
  /** Vrai si le serveur a mémorisé cette route pour le prix. */
  memorisee: boolean
}

export interface EtapeTournee {
  course: string
  code: string
  type: 'collecte' | 'livraison'
  arrivee_s: number
  distance_km: number
  lat: number | null
  lng: number | null
}

export interface Tournee {
  livreur: string
  depart: { source: 'position' | 'premiere_collecte'; fix_time?: string; lat: number; lng: number } | null
  etapes: EtapeTournee[]
  duree_s: number
  distance_km: number
  non_planifiees: string[]
  sans_position: string[]
  trace: string | null
  trace_precision: number
}

/** Clé d'un trajet au 1/100 000 de degré : la même que celle de la base (0066). */
export const cleTrajet = (de: Point, a: Point): string =>
  [de.lat, de.lng, a.lat, a.lng].map((x) => (Math.round(x * 1e5) / 1e5).toFixed(5)).join(',')

/**
 * La route à moto entre deux points. Avec `org`, le serveur la mémorise pour le prix (équipe de l'organisation seulement).
 * Ne lève jamais : sans réponse, rend l'estimation de secours, comme la base.
 */
export async function trajetRoute(de: Point, a: Point, org?: string): Promise<Trajet> {
  try {
    return await appelerFonction<Trajet>('itineraire', { action: 'trajet', de, a, ...(org ? { org } : {}) })
  } catch {
    return { distance_km: distanceRepliKm(de.lat, de.lng, a.lat, a.lng), duree_s: null, trace: null, trace_precision: 6, source: 'estimation', memorisee: false }
  }
}

/** Trajets déjà mémorisés pendant cette session : inutile de les redemander à la création de la course. */
const memorises = new Set<string>()

/** Fait mémoriser la route d'une course qui va naître (prix sur la vraie route). Jamais bloquant au-delà du délai du service. */
export async function preparerRouteCourse(org: string, de: Point, a: Point): Promise<Trajet | null> {
  const cle = cleTrajet(de, a)
  if (memorises.has(cle)) return null
  const t = await trajetRoute(de, a, org)
  if (t.memorisee) memorises.add(cle)
  return t
}

/** Retenir qu'un trajet est mémorisé côté serveur (réponse de l'aperçu). */
export function noterRouteMemorisee(de: Point, a: Point): void {
  memorises.add(cleTrajet(de, a))
}

/** L'ordre de passage optimisé des courses en cours d'un livreur (collecte avant livraison), avec les heures d'arrivée. */
export function tourneeLivreur(livreur: string): Promise<Tournee> {
  return appelerFonction<Tournee>('itineraire', { action: 'tournee', livreur })
}

/** Décode un tracé « polyline » (précision 5 pour une tournée, 6 pour un trajet) en points [lat, lng]. Tracé abîmé : ce qui a pu être lu. */
export function decoderTrace(code: string, precision: number): [number, number][] {
  const facteur = 10 ** precision
  const points: [number, number][] = []
  let i = 0
  let lat = 0
  let lng = 0
  const lire = (): number | null => {
    let resultat = 0
    let decalage = 0
    for (;;) {
      if (i >= code.length) return null
      const b = code.charCodeAt(i++) - 63
      resultat |= (b & 0x1f) << decalage
      decalage += 5
      if (b < 0x20) break
    }
    return resultat & 1 ? ~(resultat >> 1) : resultat >> 1
  }
  while (i < code.length) {
    const dLat = lire()
    const dLng = lire()
    if (dLat === null || dLng === null) break
    lat += dLat
    lng += dLng
    points.push([lat / facteur, lng / facteur])
  }
  return points
}

/** « 1 h 05 », « 12 min », « moins d'une minute ». */
export function dureeLisible(s: number): string {
  const min = Math.round(s / 60)
  if (min < 1) return 'moins d’une minute'
  if (min < 60) return `${min} min`
  return `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')}`
}

/** Ce que la carte du dispatch dessine par-dessus les livreurs : un itinéraire (course) ou une tournée (livreur). */
export interface TraceCarte {
  titre: string
  points: [number, number][]
  /** Arrêts numérotés dans l'ordre de passage. */
  arrets: { lat: number; lng: number; numero: number; type: 'collecte' | 'livraison'; libelle: string }[]
  /** Point de départ (position du livreur), s'il est connu. */
  depart: { lat: number; lng: number } | null
}

/** La tournée en tracé de carte : la ligne de VROOM, sinon les arrêts reliés à vol d'oiseau. */
export function traceDeTournee(t: Tournee): TraceCarte {
  const arrets = t.etapes
    .filter((e) => e.lat !== null && e.lng !== null)
    .map((e, i) => ({ lat: e.lat as number, lng: e.lng as number, numero: i + 1, type: e.type, libelle: `${e.type === 'collecte' ? 'Collecte' : 'Livraison'} ${e.code}` }))
  const depart = t.depart ? { lat: t.depart.lat, lng: t.depart.lng } : null
  const ligne = t.trace ? decoderTrace(t.trace, t.trace_precision) : []
  return {
    titre: `Tournée de ${t.livreur}`,
    points: ligne.length > 1 ? ligne : [...(depart ? [[depart.lat, depart.lng] as [number, number]] : []), ...arrets.map((a) => [a.lat, a.lng] as [number, number])],
    arrets,
    depart,
  }
}
